import { startOfDay, parseISO, differenceInDays, format } from 'date-fns';
import type { PlannerEvent } from '../types/planner';

export type CountdownCategory = 'all' | 'birthday' | 'holiday' | 'milestone' | 'countdown';

/**
 * Single source of truth for whether an event qualifies as a Countdown item.
 * An event is a countdown if:
 * 1. `isCountdown` is explicitly true (Special Event & Countdown toggle in Planner)
 * 2. `type` is 'birthday' (Family birthdays automatically count down)
 * 3. `type` is 'holiday' (Holidays, trips, vacations automatically count down)
 */
export function isCountdownEvent(event: PlannerEvent | any): boolean {
  if (!event) return false;
  return Boolean(
    event.isCountdown ||
    event.is_countdown ||
    event.type === 'birthday' ||
    event.type === 'holiday'
  );
}

/**
 * Maps a PlannerEvent to one of 4 synchronized countdown categories:
 * - 'birthday': 🎂 Birthdays
 * - 'holiday': ✈️ Holidays & Trips
 * - 'milestone': 🎉 Celebrations & Milestones (social/party/arts)
 * - 'countdown': ⏳ Custom / Special Countdowns (exams, sports finals, appointments, other)
 */
export function getCountdownCategory(event: PlannerEvent): 'birthday' | 'holiday' | 'milestone' | 'countdown' {
  if (event.type === 'birthday') return 'birthday';
  if (event.type === 'holiday') return 'holiday';
  if (event.type === 'social' || event.type === 'arts') return 'milestone';
  return 'countdown';
}

export interface CountdownCalculation {
  targetDate: Date;
  targetDateStr: string;
  daysLeft: number;
  hoursLeft: number;
  minutesLeft: number;
  secondsLeft: number;
  diffMs: number;
  isUnder24Hours: boolean;
  isOngoing: boolean;
  emoji: string;
  category: 'birthday' | 'holiday' | 'milestone' | 'countdown';
  typeLabel: string;
  tagColor: string;
  isBirthday: boolean;
  isHoliday: boolean;
  isMilestone: boolean;
}

export function calculateCountdownDetails(event: PlannerEvent, now: Date = new Date()): CountdownCalculation | null {
  if (!isCountdownEvent(event)) return null;

  const today = startOfDay(now);
  const rawStart = parseISO(event.startDate);
  if (isNaN(rawStart.getTime())) return null;

  const category = getCountdownCategory(event);
  const isBirthday = category === 'birthday';
  const isHoliday = category === 'holiday';
  const isMilestone = category === 'milestone';

  let targetDate: Date = rawStart;
  let isOngoing = false;

  // 1. Birthdays: Annual recurring date rollover
  if (isBirthday) {
    const cleanDateStr = (event.startDate || '').split('T')[0];
    const parts = cleanDateStr.split('-').map(Number);
    const bMonth = (parts[1] || 1) - 1;
    const bDay = parts[2] || 1;

    let candidate = new Date(today.getFullYear(), bMonth, bDay, 0, 0, 0, 0);
    if (differenceInDays(candidate, today) < 0) {
      candidate = new Date(today.getFullYear() + 1, bMonth, bDay, 0, 0, 0, 0);
    }
    targetDate = candidate;
  }
  // 2. Multi-day or in-progress check
  else if (differenceInDays(rawStart, today) < 0) {
    const rawEnd = event.endDate ? parseISO(event.endDate) : rawStart;
    if (!isNaN(rawEnd.getTime()) && rawEnd >= today) {
      targetDate = today;
      isOngoing = true;
    } else {
      // Past event, omit from active countdowns
      return null;
    }
  }

  const targetDay = startOfDay(targetDate);
  const daysLeft = Math.max(0, Math.round((targetDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

  let targetDateTime = targetDay;
  if (event.startTime) {
    const [h, m] = event.startTime.split(':').map(Number);
    targetDateTime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), h || 0, m || 0, 0);
  }

  const diffMs = targetDateTime.getTime() - now.getTime();
  const isUnder24Hours = daysLeft === 0 || (diffMs > 0 && diffMs < 24 * 60 * 60 * 1000);

  const hoursLeft = Math.max(0, Math.floor((Math.max(0, diffMs) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
  const minutesLeft = Math.max(0, Math.floor((Math.max(0, diffMs) % (1000 * 60 * 60)) / (1000 * 60)));
  const secondsLeft = Math.max(0, Math.floor((Math.max(0, diffMs) % (1000 * 60)) / 1000));

  // Determine contextual emoji and styling from event details
  const titleLower = (event.title || '').toLowerCase();
  let emoji = '⏳';
  let typeLabel = 'Countdown';
  let tagColor = 'bg-violet-500/15 text-violet-600 dark:text-violet-300 border-violet-500/20';

  if (isBirthday) {
    emoji = '🎂';
    typeLabel = 'Birthday';
    tagColor = 'bg-pink-500/15 text-pink-600 dark:text-pink-300 border-pink-500/20';
  } else if (isHoliday) {
    if (titleLower.includes('cruise')) emoji = '🚢';
    else if (titleLower.includes('flight') || titleLower.includes('fly')) emoji = '✈️';
    else if (titleLower.includes('camp')) emoji = '⛺';
    else if (titleLower.includes('beach') || titleLower.includes('summer')) emoji = '🏖️';
    else emoji = '✈️';
    typeLabel = 'Holiday / Trip';
    tagColor = 'bg-teal-500/15 text-teal-600 dark:text-teal-300 border-teal-500/20';
  } else if (isMilestone) {
    if (titleLower.includes('anniversary') || titleLower.includes('wedding')) emoji = '💍';
    else if (titleLower.includes('grad')) emoji = '🎓';
    else if (titleLower.includes('baby') || titleLower.includes('shower')) emoji = '🍼';
    else emoji = '🎉';
    typeLabel = 'Celebration';
    tagColor = 'bg-purple-500/15 text-purple-600 dark:text-purple-300 border-purple-500/20';
  } else if (event.type === 'school') {
    emoji = '📚';
    typeLabel = 'School / Exam';
    tagColor = 'bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/20';
  } else if (event.type === 'sport') {
    emoji = '🏆';
    typeLabel = 'Sports Final';
    tagColor = 'bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/20';
  } else if (event.type === 'appointment') {
    emoji = '🏥';
    typeLabel = 'Appointment';
    tagColor = 'bg-rose-500/15 text-rose-600 dark:text-rose-300 border-rose-500/20';
  }

  return {
    targetDate,
    targetDateStr: format(targetDate, 'yyyy-MM-dd'),
    daysLeft,
    hoursLeft,
    minutesLeft,
    secondsLeft,
    diffMs,
    isUnder24Hours,
    isOngoing,
    emoji,
    category,
    typeLabel,
    tagColor,
    isBirthday,
    isHoliday,
    isMilestone,
  };
}
